import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, CheckCircle, XCircle, Clock, AlertCircle, Inbox, Calendar, FileText, ArrowUpDown, Eye, ChevronLeft, ChevronRight } from '../../icons/wp-icons';
import { kiZentraleApi } from '../../api/kiZentraleApi';
import apiClient from '../../api/apiClient';
import { useNotifications } from '../../context/NotificationContext';
import { useKiTodos } from '../../context/KiTodoContext';
import { notificationApi } from '../../api/notificationApi';
import CounterProposalModal from '../Notifications/CounterProposalModal';
import toast from 'react-hot-toast';
import { useEmbed } from '../../hooks/useEmbed';
import { censorSubject, censorBody } from '../../utils/demoCensor';

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

const KANAL_CONFIG = {
  whatsapp: { label: 'WhatsApp', icon: WhatsAppIcon, bgColor: 'bg-green-50', textColor: 'text-green-700', badgeBg: 'bg-green-100', badgeText: 'text-green-700' },
  telefon: { label: 'Telefon', icon: Phone, bgColor: 'bg-purple-50', textColor: 'text-purple-700', badgeBg: 'bg-purple-100', badgeText: 'text-purple-700' },
  email: { label: 'E-Mail', icon: Mail, bgColor: 'bg-blue-50', textColor: 'text-blue-700', badgeBg: 'bg-blue-100', badgeText: 'text-blue-700' },
  sonstige: { label: 'Sonstige', icon: AlertCircle, bgColor: 'bg-gray-50', textColor: 'text-gray-700', badgeBg: 'bg-gray-100', badgeText: 'text-gray-700' },
};

const TABS = [
  { key: 'alle', label: 'Alle' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'telefon', label: 'Telefon' },
  { key: 'email', label: 'E-Mail' },
];

const SORT_OPTIONS = [
  { key: 'date_desc', label: 'Neueste zuerst' },
  { key: 'date_asc', label: 'Älteste zuerst' },
  { key: 'priority_desc', label: 'Priorität hoch → niedrig' },
  { key: 'priority_asc', label: 'Priorität niedrig → hoch' },
];

function formatTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  if (diffHours < 24) return `vor ${diffHours} Std.`;
  if (diffDays === 1) return 'Gestern';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export default function KiTodos() {
  const { isEmbed } = useEmbed();
  const [todos, setTodos] = useState([]);
  const [counts, setCounts] = useState({ alle: 0, whatsapp: 0, telefon: 0, email: 0 });
  const [activeTab, setActiveTab] = useState('alle');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date_desc');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const { decrementUnread } = useNotifications();
  const { refreshTodoCount, decrementTodoCount } = useKiTodos();
  const navigate = useNavigate();

  const loadTodos = useCallback(async () => {
    try {
      const params = sortBy !== 'date_desc' ? { sortBy } : {};
      const data = await kiZentraleApi.getTodos(params);
      setTodos(data.todos || []);
      setCounts(data.counts || { alle: 0, whatsapp: 0, telefon: 0, email: 0 });
    } catch (err) {
      console.error('KI Todos laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => {
    loadTodos();
    const interval = setInterval(loadTodos, 30000);
    return () => clearInterval(interval);
  }, [loadTodos]);

  const handleConfirm = async (todoId) => {
    setActionLoading(prev => ({ ...prev, [todoId]: 'confirm' }));
    try {
      await apiClient.post(`/notifications/${todoId}/confirm`);
      setTodos(prev => prev.filter(t => t.id !== todoId));
      setCounts(prev => {
        const todo = todos.find(t => t.id === todoId);
        const kanal = todo?.kanal || 'sonstige';
        return {
          ...prev,
          alle: Math.max(0, prev.alle - 1),
          ...(prev[kanal] !== undefined ? { [kanal]: Math.max(0, prev[kanal] - 1) } : {}),
        };
      });
      decrementUnread();
      decrementTodoCount();
    } catch (err) {
      console.error('Bestätigen fehlgeschlagen:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [todoId]: null }));
    }
  };

  const handleReject = async (todoId) => {
    setActionLoading(prev => ({ ...prev, [todoId]: 'reject' }));
    try {
      await apiClient.post(`/notifications/${todoId}/reject`);
      setTodos(prev => prev.filter(t => t.id !== todoId));
      setCounts(prev => {
        const todo = todos.find(t => t.id === todoId);
        const kanal = todo?.kanal || 'sonstige';
        return {
          ...prev,
          alle: Math.max(0, prev.alle - 1),
          ...(prev[kanal] !== undefined ? { [kanal]: Math.max(0, prev[kanal] - 1) } : {}),
        };
      });
      decrementUnread();
      decrementTodoCount();
    } catch (err) {
      console.error('Ablehnen fehlgeschlagen:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [todoId]: null }));
    }
  };

  const handleMarkAsRead = async (todoId) => {
    setActionLoading(prev => ({ ...prev, [todoId]: 'read' }));
    try {
      await notificationApi.markAsRead(todoId);
      setTodos(prev => prev.filter(t => t.id !== todoId));
      setCounts(prev => {
        const todo = todos.find(t => t.id === todoId);
        const kanal = todo?.kanal || 'sonstige';
        return {
          ...prev,
          alle: Math.max(0, prev.alle - 1),
          ...(prev[kanal] !== undefined ? { [kanal]: Math.max(0, prev[kanal] - 1) } : {}),
        };
      });
      decrementUnread();
      decrementTodoCount();
    } catch (err) {
      console.error('Als gelesen markieren fehlgeschlagen:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [todoId]: null }));
    }
  };

  // Gegenvorschlag
  const [counterProposalTodo, setCounterProposalTodo] = useState(null);
  const [counterProposalLoading, setCounterProposalLoading] = useState(false);

  const handleCounterPropose = (todo) => {
    setCounterProposalTodo(todo);
  };

  const handleSubmitCounterProposal = async ({ datum, uhrzeit }) => {
    if (!counterProposalTodo) return;
    setCounterProposalLoading(true);
    try {
      await apiClient.post(`/notifications/${counterProposalTodo.id}/counter-propose`, { datum, uhrzeit });
      setTodos(prev => prev.filter(t => t.id !== counterProposalTodo.id));
      setCounts(prev => {
        const kanal = counterProposalTodo.kanal || 'sonstige';
        return {
          ...prev,
          alle: Math.max(0, prev.alle - 1),
          ...(prev[kanal] !== undefined ? { [kanal]: Math.max(0, prev[kanal] - 1) } : {}),
        };
      });
      decrementUnread();
      decrementTodoCount();
      toast.success(`Gegenvorschlag (${datum} um ${uhrzeit} Uhr) gesendet.`);
      setCounterProposalTodo(null);
    } catch (err) {
      console.error('Counter-propose fehlgeschlagen:', err);
      toast.error('Fehler: ' + (err.response?.data?.error || err.message));
    } finally {
      setCounterProposalLoading(false);
    }
  };

  const ITEMS_PER_PAGE = 10;

  const filteredTodos = activeTab === 'alle'
    ? todos
    : todos.filter(t => t.kanal === activeTab);

  const totalPages = Math.max(1, Math.ceil(filteredTodos.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedTodos = filteredTodos.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-5">
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (counts.alle === 0) return null;

  return (
    <div className="bg-surface rounded-card shadow-card p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-text">KI Todos</h3>
          <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-primary text-white">
            {counts.alle}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <ArrowUpDown className="w-3.5 h-3.5 text-text-muted" />
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
            className="text-xs bg-gray-100 border border-border rounded-md px-2 py-1 text-text-muted hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
        {TABS.map(tab => {
          const count = tab.key === 'alle' ? counts.alle : (counts[tab.key] || 0);
          const isActive = activeTab === tab.key;
          if (tab.key !== 'alle' && count === 0) return null;

          return (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                isActive
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-text-muted hover:bg-gray-200'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-xs rounded-full ${
                  isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Todo List */}
      <div className="space-y-2">
        {filteredTodos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-text-muted">
            <Inbox className="w-8 h-8 mb-2 opacity-40" />
            <p className="text-sm">Keine offenen Todos in diesem Kanal</p>
          </div>
        ) : (
          paginatedTodos.map(todo => {
            const config = KANAL_CONFIG[todo.kanal] || KANAL_CONFIG.sonstige;
            const Icon = config.icon;
            const isConfirming = actionLoading[todo.id] === 'confirm';
            const isRejecting = actionLoading[todo.id] === 'reject';
            const isDisabled = !!actionLoading[todo.id];

            return (
              <div
                key={todo.id}
                className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-gray-50/50 transition"
              >
                {/* Kanal Icon */}
                <div className={`p-2 rounded-lg shrink-0 ${config.bgColor}`}>
                  <Icon className={`w-4 h-4 ${config.textColor}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {todo.priority === 'HIGH' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-700 shrink-0">Hoch</span>
                      )}
                      {todo.priority === 'LOW' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-gray-100 text-gray-500 shrink-0">Niedrig</span>
                      )}
                      <p className="text-sm font-medium text-text truncate">{censorSubject(todo.titel, isEmbed)}</p>
                    </div>
                    <span className="text-xs text-text-muted whitespace-nowrap shrink-0 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(todo.erstelltAm)}
                    </span>
                  </div>
                  {todo.nachricht && (
                    <p className="text-xs text-text-muted mt-0.5 line-clamp-2">{censorBody(todo.nachricht, isEmbed)}</p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {todo.hatAktion && (
                      <>
                        <button
                          onClick={() => handleConfirm(todo.id)}
                          disabled={isDisabled}
                          className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition disabled:opacity-50"
                        >
                          {isConfirming ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-700"></div>
                          ) : (
                            <CheckCircle className="w-3 h-3" />
                          )}
                          {todo.confirmLabel || 'Bestätigen'}
                        </button>
                        {todo.dataTyp === 'KVA_BESTAETIGUNG' && (
                          <button
                            onClick={() => {
                              const kvaBestaetungId = todo.kvaBestaetungId;
                              if (kvaBestaetungId) {
                                setActionLoading(prev => ({ ...prev, [todo.id]: 'edit' }));
                                apiClient.post(`/kva-bestaetigung/${kvaBestaetungId}/approve-as-draft`)
                                  .then(res => {
                                    const kvaId = res.data.kva?.id;
                                    if (kvaId) {
                                      toast.success('KVA als Entwurf erstellt');
                                      navigate(`/kostenvoranschlaege/${kvaId}/bearbeiten`);
                                    }
                                    loadTodos();
                                  })
                                  .catch(err => toast.error('Fehler: ' + (err.response?.data?.error || err.message)))
                                  .finally(() => setActionLoading(prev => ({ ...prev, [todo.id]: null })));
                              }
                            }}
                            disabled={isDisabled}
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition disabled:opacity-50"
                          >
                            <FileText className="w-3 h-3" />
                            Bearbeiten
                          </button>
                        )}
                        {todo.dataTyp !== 'STORNIERUNG' && todo.dataTyp !== 'KVA_BESTAETIGUNG' && (
                          <button
                            onClick={() => handleCounterPropose(todo)}
                            disabled={isDisabled}
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition disabled:opacity-50"
                          >
                            <Calendar className="w-3 h-3" />
                            Alternativ
                          </button>
                        )}
                        <button
                          onClick={() => handleReject(todo.id)}
                          disabled={isDisabled}
                          className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition disabled:opacity-50"
                        >
                          {isRejecting ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-700"></div>
                          ) : (
                            <XCircle className="w-3 h-3" />
                          )}
                          {todo.rejectLabel || 'Ablehnen'}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleMarkAsRead(todo.id)}
                      disabled={isDisabled}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 transition disabled:opacity-50"
                    >
                      {actionLoading[todo.id] === 'read' ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
                      ) : (
                        <Eye className="w-3 h-3" />
                      )}
                      Gelesen
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
          <span className="text-xs text-text-muted">
            {(safePage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(safePage * ITEMS_PER_PAGE, filteredTodos.length)} von {filteredTodos.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="p-1.5 rounded-md text-text-muted hover:bg-gray-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-7 h-7 rounded-md text-xs font-medium transition ${
                  page === safePage
                    ? 'bg-primary text-white'
                    : 'text-text-muted hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="p-1.5 rounded-md text-text-muted hover:bg-gray-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <CounterProposalModal
        isOpen={!!counterProposalTodo}
        onClose={() => setCounterProposalTodo(null)}
        onSubmit={handleSubmitCounterProposal}
        isLoading={counterProposalLoading}
        originalTermin={counterProposalTodo?.terminDaten
          ? `${counterProposalTodo.terminDaten.datum} um ${counterProposalTodo.terminDaten.uhrzeit} Uhr`
          : null}
      />
    </div>
  );
}
